import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "input",
    "zone",
    "fileLabel",
    "submit",
    "previewSection",
    "previewError",
    "previewTableBody",
    "hiddenFields",
    "facilityHeader",
    "matrixHeader",
    "emailHeader",
  ];

  static values = {
    previewUrl: String,
    emptyPreviewMessage: String,
  };

  connect() {
    this._defaultSubmitTitle = this.hasSubmitTarget
      ? this.submitTarget.getAttribute("title") || ""
      : "";
    this.detectedIdType = null;
    this._rowsReady = false;
    this.boundBuyerChanged = () => this._syncSubmitState(this._rowsReady);
    this.element.addEventListener("change", this.boundBuyerChanged);
    this._syncSubmitState(false);
  }

  disconnect() {
    this.element.removeEventListener("change", this.boundBuyerChanged);
  }

  browse() {
    this.inputTarget.click();
  }

  onFileChange() {
    const file = this.inputTarget.files?.[0];
    if (this.hasFileLabelTarget) {
      this.fileLabelTarget.textContent = file ? file.name : "";
    }
    this.detectedIdType = null;
    this._clearHiddenFields();
    this._syncSubmitState(false);
    void this._fetchPreview();
  }

  dragOver(event) {
    event.preventDefault();
    event.stopPropagation();
    this.zoneTarget.classList.add("border-brand-400", "bg-brand-25");
  }

  dragLeave(event) {
    event.preventDefault();
    this.zoneTarget.classList.remove("border-brand-400", "bg-brand-25");
  }

  drop(event) {
    event.preventDefault();
    event.stopPropagation();
    this.zoneTarget.classList.remove("border-brand-400", "bg-brand-25");

    const file = event.dataTransfer?.files?.[0];
    if (!file || !this._accepts(file)) return;

    const dt = new DataTransfer();
    dt.items.add(file);
    this.inputTarget.files = dt.files;
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  currentIdType() {
    return this.detectedIdType || "facility";
  }

  async _fetchPreview() {
    const file = this.inputTarget.files?.[0];
    if (!file || !this.previewUrlValue) {
      this._hidePreview();
      return;
    }

    this._showLoading();

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const body = new FormData();
    body.append("file", file);

    try {
      const res = await fetch(this.previewUrlValue, {
        method: "POST",
        body,
        headers: {
          Accept: "application/json",
          ...(token ? { "X-CSRF-Token": token } : {}),
        },
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data) {
        const msg =
          data?.error || "Could not read the spreadsheet. Please try again.";
        this._showPreviewError(msg);
        this._clearHiddenFields();
        this._syncSubmitState(false);
        return;
      }

      this.detectedIdType = data.id_type === "matrix" ? "matrix" : "facility";
      this._syncPreviewHeaders();

      const rows = Array.isArray(data.rows) ? data.rows : [];
      this._renderPreviewRows(rows);
      this._populateHiddenFields(rows);
      this._syncSubmitState(rows.length > 0);
    } catch {
      this._showPreviewError(
        "Could not load a preview. Check your connection and try again.",
      );
      this._clearHiddenFields();
      this._syncSubmitState(false);
    }
  }

  _syncPreviewHeaders() {
    const isMatrix = this.currentIdType() === "matrix";

    if (this.hasFacilityHeaderTarget) {
      this.facilityHeaderTarget.classList.remove("hidden");
    }
    if (this.hasMatrixHeaderTarget) {
      this.matrixHeaderTarget.classList.toggle("hidden", !isMatrix);
    }
    if (this.hasEmailHeaderTarget) {
      this.emailHeaderTarget.classList.toggle("hidden", isMatrix);
    }
  }

  _syncSubmitState(enabled) {
    if (!this.hasSubmitTarget) return;

    this._rowsReady = enabled;
    const ready = enabled && this._buyerSelected();
    this.submitTarget.disabled = !ready;
    this.submitTarget.classList.toggle("cursor-not-allowed", !ready);
    this.submitTarget.classList.toggle("opacity-60", !ready);

    if (ready) {
      this.submitTarget.removeAttribute("title");
    } else {
      this.submitTarget.setAttribute("title", this._defaultSubmitTitle);
    }
  }

  _buyerSelected() {
    const select = this.element.querySelector('select[name="invite[buyer_id]"]');
    if (!select) return true;

    return select.value.trim() !== "";
  }

  _populateHiddenFields(rows) {
    this._clearHiddenFields();
    if (!this.hasHiddenFieldsTarget) return;

    const idTypeField = document.createElement("input");
    idTypeField.type = "hidden";
    idTypeField.name = "invite[id_type]";
    idTypeField.value = this.currentIdType();
    this.hiddenFieldsTarget.appendChild(idTypeField);

    rows.forEach((row) => {
      const fid = document.createElement("input");
      fid.type = "hidden";
      fid.name = "invite[entries][][facility_id]";
      fid.value = row.facility_id ?? "";

      const email = document.createElement("input");
      email.type = "hidden";
      email.name = "invite[entries][][email]";
      email.value = row.email ?? "";

      const calculationId = document.createElement("input");
      calculationId.type = "hidden";
      calculationId.name = "invite[entries][][calculation_id]";
      calculationId.value = row.calculation_id ?? "";

      this.hiddenFieldsTarget.appendChild(fid);
      this.hiddenFieldsTarget.appendChild(email);
      this.hiddenFieldsTarget.appendChild(calculationId);
    });
  }

  _clearHiddenFields() {
    if (this.hasHiddenFieldsTarget) {
      this.hiddenFieldsTarget.innerHTML = "";
    }
  }

  _showLoading() {
    if (!this.hasPreviewSectionTarget) return;

    this.previewSectionTarget.classList.remove("hidden");
    if (this.hasPreviewErrorTarget) {
      this.previewErrorTarget.classList.add("hidden");
      this.previewErrorTarget.textContent = "";
    }
    if (this.hasPreviewTableBodyTarget) {
      this.previewTableBodyTarget.innerHTML = "";
      const tr = document.createElement("tr");
      const td = document.createElement("td");
      td.colSpan = 2;
      td.className =
        "px-2 py-6 text-center text-sm text-gray-modern-500 sm:px-3";
      td.textContent = "Reading spreadsheet…";
      tr.appendChild(td);
      this.previewTableBodyTarget.appendChild(tr);
    }
  }

  _hidePreview() {
    if (!this.hasPreviewSectionTarget) return;
    this.previewSectionTarget.classList.add("hidden");
  }

  _showPreviewError(message) {
    if (!this.hasPreviewSectionTarget) return;
    this.previewSectionTarget.classList.remove("hidden");
    if (this.hasPreviewErrorTarget) {
      this.previewErrorTarget.textContent = message;
      this.previewErrorTarget.classList.remove("hidden");
    }
    if (this.hasPreviewTableBodyTarget) {
      this.previewTableBodyTarget.innerHTML = "";
    }
  }

  _renderPreviewRows(rows) {
    if (!this.hasPreviewSectionTarget || !this.hasPreviewTableBodyTarget)
      return;

    if (this.hasPreviewErrorTarget) {
      this.previewErrorTarget.classList.add("hidden");
    }

    this.previewTableBodyTarget.innerHTML = "";

    if (rows.length === 0) {
      const tr = document.createElement("tr");
      const td = document.createElement("td");
      td.colSpan = 2;
      td.className =
        "px-2 py-6 text-center text-sm text-gray-modern-500 sm:px-3";
      td.textContent =
        this.emptyPreviewMessageValue ||
        "The spreadsheet contains no data rows.";
      tr.appendChild(td);
      this.previewTableBodyTarget.appendChild(tr);
    } else {
      rows.forEach((row) => {
        this.previewTableBodyTarget.appendChild(this._buildRow(row));
      });
    }

    this.previewSectionTarget.classList.remove("hidden");
  }

  _buildRow(row) {
    const INPUT_CLASS =
      "block w-full cursor-default rounded-lg border border-gray-modern-200 bg-gray-modern-50 px-3 py-2.5 text-sm text-gray-modern-900";

    const tr = document.createElement("tr");
    tr.className = "align-top";
    const idType = this.currentIdType();

    const tdFacility = document.createElement("td");
    tdFacility.className = "px-2 py-3 sm:px-3";
    const facilityInput = document.createElement("input");
    facilityInput.type = "text";
    facilityInput.readOnly = true;
    facilityInput.tabIndex = -1;
    facilityInput.value = row.facility_id ?? "";
    facilityInput.className = INPUT_CLASS;
    tdFacility.appendChild(facilityInput);
    tr.appendChild(tdFacility);

    if (idType === "matrix") {
      const tdMatrix = document.createElement("td");
      tdMatrix.className = "px-2 py-3 sm:px-3";
      const matrixInput = document.createElement("input");
      matrixInput.type = "text";
      matrixInput.readOnly = true;
      matrixInput.tabIndex = -1;
      matrixInput.value = row.calculation_id ?? "";
      matrixInput.className = INPUT_CLASS;
      tdMatrix.appendChild(matrixInput);
      tr.appendChild(tdMatrix);
    } else {
      const tdEmail = document.createElement("td");
      tdEmail.className = "px-2 py-3 sm:px-3";
      const emailInput = document.createElement("input");
      emailInput.type = "email";
      emailInput.readOnly = true;
      emailInput.tabIndex = -1;
      emailInput.value = row.email ?? "";
      emailInput.className = INPUT_CLASS;
      tdEmail.appendChild(emailInput);
      tr.appendChild(tdEmail);
    }

    return tr;
  }

  _accepts(file) {
    const name = file.name.toLowerCase();
    return name.endsWith(".xlsx") || name.endsWith(".xls");
  }
}
